import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import type { StartImpersonationInput } from '@platform/api-client';
import { SESSION_QUERY_KEY, useApiClient, useSession } from '@platform/api-client/react';
import { ImpersonationBanner, useToast } from '@platform/ui';

import { describeApiError } from './session/describe-error';
import { isTrustedRedirect } from './session/next-path';
import { useStepUp } from './session/step-up';

/**
 * Impersonation, for real (M2.8, M12.5, ADR-005, SECURITY.md §6.3, FR-SUPER-06).
 *
 * This replaces the M12.7 mock. Nothing is held in React state any more: the banner is
 * drawn from `session.impersonation`, which the API re-reads from the
 * `impersonation_sessions` row on **every** request. That matters more than it looks -
 * a support session that is revoked or that times out server-side stops showing a banner
 * on the next session read, rather than leaving a stale one on screen claiming an access
 * the operator no longer has.
 *
 * Starting is a step-up endpoint, so `start` goes through `useStepUp().guard` and the
 * whole call - reason and all - is retried after the second factor verifies.
 */

export interface ImpersonationValue {
  /** The banner payload, or null when this session is not impersonating. */
  session: {
    impersonatorEmail: string;
    startedAt: string;
    expiresAt: string;
    blockedPermissions: readonly string[];
  } | null;
  /** Who is being viewed as, when the session knows the name. */
  targetName: string | null;
  tenantName: string | null;
}

/** Everything the banner needs, straight from `GET /auth/session`. */
export function useImpersonation(): ImpersonationValue {
  const session = useSession();
  const data = session.data;
  const impersonation = data?.impersonation ?? null;

  if (impersonation === null) return { session: null, targetName: null, tenantName: null };

  return {
    session: {
      impersonatorEmail: impersonation.impersonatorEmail,
      startedAt: impersonation.startedAt,
      expiresAt: impersonation.expiresAt,
      blockedPermissions: impersonation.blockedPermissions,
    },
    // While impersonating, `user` is the *target* - that is the whole point of the
    // derived session - so the banner's "viewing as" comes from there.
    targetName: data?.user?.name ?? data?.user?.email ?? null,
    tenantName: data?.tenant?.name ?? null,
  };
}

export interface StartImpersonation {
  start: (input: StartImpersonationInput) => Promise<void>;
  pending: boolean;
}

/**
 * Starts a support session.
 *
 * On success the API answers a `redirectUrl` into the tenant's **admin** host, because
 * that is where the operator actually needs to be - this console has no clinic screens.
 * The URL comes from our own API and is still checked before the browser follows it
 * (`isTrustedRedirect`), because "the server told us to" is how open redirects get
 * shipped. When there is no URL to follow, or it is not one we trust, the session is
 * refreshed instead and the banner appears here.
 */
export function useStartImpersonation(): StartImpersonation {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { guard } = useStepUp();
  const { toast } = useToast();
  const [pending, setPending] = useState(false);

  const start = useCallback(
    async (input: StartImpersonationInput): Promise<void> => {
      setPending(true);
      try {
        const result = await guard(
          () => api.auth.impersonationStart(input),
          'start a support session',
        );

        const redirect = result.redirectUrl;
        if (
          redirect !== undefined &&
          redirect !== '' &&
          isTrustedRedirect(redirect, window.location)
        ) {
          window.location.assign(redirect);
          return;
        }

        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY, refetchType: 'all' });
        if (redirect !== undefined && redirect !== '') {
          toast({
            tone: 'warning',
            message:
              'The support session started, but the clinic address was not one we recognise.',
          });
        }
      } catch (caught) {
        toast({ tone: 'error', message: describeApiError(caught) });
      } finally {
        setPending(false);
      }
    },
    [api, guard, queryClient, toast],
  );

  return { start, pending };
}

/**
 * The banner, wired to the session.
 *
 * Exit is `DELETE /platform/impersonation/current`. That endpoint is deliberately
 * unpermissioned and MFA-exempt on the server, because inside a support session the caller
 * holds the *target's* permissions - leaving must never be harder than entering
 * (rules §20.6).
 *
 * It answers **`200 { redirectUrl }`**, not `204` (M12.5, API.md §2.13 note 4): the exit is
 * usually pressed inside the clinic's admin app, on the clinic's host, and the session that
 * took the browser there has just been deleted - a `204` would leave it nowhere to go. The
 * URL is the console's own host, derived from the API's `PLATFORM_DOMAIN` and never from a
 * request header, and it is still checked before the browser follows it
 * (`isTrustedRedirect`): "the server told us to" is how open redirects get shipped.
 *
 * `api.auth.impersonationStop()` answers `{ redirectUrl }`; it returns `''` rather than
 * `undefined` when the answer carried no body, so there is one falsy case to handle here.
 */
export function ImpersonationBannerConnected({ now }: { now?: () => number }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session, targetName, tenantName } = useImpersonation();
  const [exiting, setExiting] = useState(false);

  const exit = useCallback((): void => {
    setExiting(true);
    void (async () => {
      let redirect = '';
      try {
        redirect = (await api.auth.impersonationStop()).redirectUrl;
      } catch (caught) {
        toast({ tone: 'error', message: describeApiError(caught) });
      }

      if (redirect !== '' && isTrustedRedirect(redirect, window.location)) {
        window.location.assign(redirect);
        return;
      }

      // Refresh either way. If the session had already ended server-side the call answers
      // 404, and the right response to that is still "re-read the session".
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY, refetchType: 'all' });
      setExiting(false);
    })();
  }, [api, queryClient, toast]);

  if (session === null) return null;

  return (
    <ImpersonationBanner
      impersonatorEmail={session.impersonatorEmail}
      expiresAt={session.expiresAt}
      blockedPermissions={session.blockedPermissions}
      {...(targetName === null ? {} : { targetLabel: targetName })}
      {...(tenantName === null ? {} : { tenantName })}
      {...(now === undefined ? {} : { now })}
      exiting={exiting}
      onExit={exit}
    />
  );
}
